<template>
  <transition name="fade">
    <div class="vod-controls" v-if="isVisible">
      <div class="vod-controls__title" v-if="title">{{ title }}</div>

      <div class="vod-controls__bar">
        <div class="vod-controls__time">{{ getElapsed }}</div>

        <div class="vod-controls__progress">
          <div
            class="vod-controls__progress-fill"
            :style="{ width: getProgress + '%' }"
          ></div>
          <div
            class="vod-controls__progress-knob"
            :style="{ left: getProgress + '%' }"
          ></div>
        </div>

        <div class="vod-controls__time">{{ getDuration }}</div>
      </div>

      <div class="vod-controls__buttons">
        <span
          v-for="button in getButtons"
          :key="button.key"
          class="vod-controls__button"
          :class="{ 'vod-controls__button--active': button.isActive }"
        >
          <span class="vod-controls__glyph">{{ button.glyph }}</span>
          <span class="vod-controls__speed" v-if="button.speed">
            {{ button.speed }}
          </span>
        </span>
      </div>

      <div class="vod-controls__hint">{{ getHint }}</div>
    </div>
  </transition>
</template>

<script>
import { PLAYER_STATE, formatTime } from '@/helpers/VodPlayer';

/**
 * Bedienleiste des VoD-Players.
 *
 * Reine Anzeige: sie nimmt KEINE Tasten entgegen und ruft den Player nicht auf.
 * Bedient wird ueber die Wiedergabetasten der Fernbedienung, ausgewertet in
 * pages/MediaPlayer.vue. Die Leiste zeigt nur, was gerade passiert - welche
 * Funktion aktiv ist und wo im Film der Gast steht.
 *
 * Gezeichnet wird mit Textzeichen statt mit Symbolgrafiken: die
 * Unicode-Wiedergabezeichen sind in den Systemschriften von Tizen und webOS
 * enthalten, ein SVG-Satz waere hier zusaetzlicher Ballast.
 */
export default {
  props: {
    isVisible: {
      type: Boolean,
      default: true,
    },
    state: {
      type: String,
      default: PLAYER_STATE.IDLE,
    },
    speed: {
      type: Number,
      default: 1,
    },
    currentTime: {
      type: Number,
      default: 0,
    },
    duration: {
      type: Number,
      default: 0,
    },
    title: {
      type: String,
      default: '',
    },
    hint: {
      type: String,
      default: '',
    },
  },
  computed: {
    getElapsed() {
      return formatTime(this.currentTime);
    },
    getDuration() {
      // Ohne bekannte Laufzeit (Live-HLS) bleibt das Feld leer statt '--:--'
      // neben einer Fortschrittsleiste zu zeigen, die ohnehin nicht fuellt.
      return this.duration ? formatTime(this.duration) : '';
    },
    getProgress() {
      if (!this.duration) return 0;
      const value = (this.currentTime / this.duration) * 100;
      return Math.min(100, Math.max(0, value));
    },
    getSpeedLabel() {
      return this.speed > 1 ? this.speed + 'x' : '';
    },
    getButtons() {
      const state = this.state;
      return [
        {
          key: 'rewind',
          glyph: '◀◀',
          isActive: state === PLAYER_STATE.REWIND,
          speed: state === PLAYER_STATE.REWIND ? this.getSpeedLabel : '',
        },
        {
          key: 'play',
          glyph: '▶',
          isActive: state === PLAYER_STATE.PLAYING,
          speed: '',
        },
        {
          key: 'pause',
          glyph: '‖',
          isActive: state === PLAYER_STATE.PAUSED,
          speed: '',
        },
        {
          key: 'stop',
          glyph: '■',
          isActive:
            state === PLAYER_STATE.STOPPED || state === PLAYER_STATE.ENDED,
          speed: '',
        },
        {
          key: 'forward',
          glyph: '▶▶',
          isActive: state === PLAYER_STATE.FORWARD,
          speed: state === PLAYER_STATE.FORWARD ? this.getSpeedLabel : '',
        },
      ];
    },
    getHint() {
      return this.hint || this.$i18n('player.hint');
    },
  },
};
</script>

<style scoped>
.vod-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 4vh 6vw 3vh;
  color: #f1f1f1;
  /* Verlauf statt Flaeche: unten deckend genug fuer die Schrift, oben
     durchsichtig, damit die Leiste nicht wie ein Balken im Bild steht. */
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0.6) 60%,
    rgba(0, 0, 0, 0) 100%
  );
  text-align: left;
  /* Ueber dem Video. Auf Tizen liegt die Videoflaeche je nach Firmware in einer
     eigenen Ebene - ohne z-index verschwindet die Leiste dahinter. */
  z-index: 10;
}

.vod-controls__title {
  font-size: 3.5vh;
  margin-bottom: 2vh;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vod-controls__bar {
  align-items: center;
  display: flex;
}

.vod-controls__time {
  flex: 0 0 auto;
  font-size: 2.4vh;
  /* Feste Breite, sonst wandert die Fortschrittsleiste bei jedem Wechsel der
     Stellenzahl (9:59 -> 10:00) um ein Zeichen hin und her. */
  min-width: 9vh;
  text-align: center;
}

.vod-controls__progress {
  background-color: rgba(241, 241, 241, 0.3);
  border-radius: 0.4vh;
  flex: 1 1 auto;
  height: 0.8vh;
  margin: 0 1.5vw;
  position: relative;
}

.vod-controls__progress-fill {
  background-color: #f1f1f1;
  border-radius: 0.4vh;
  height: 100%;
}

.vod-controls__progress-knob {
  background-color: #f1f1f1;
  border-radius: 50%;
  height: 2.2vh;
  margin-left: -1.1vh;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 2.2vh;
}

.vod-controls__buttons {
  display: flex;
  justify-content: center;
  margin-top: 2.5vh;
}

.vod-controls__button {
  align-items: center;
  border-radius: 0.8vh;
  display: flex;
  font-size: 2.8vh;
  margin: 0 0.6vw;
  min-width: 6vh;
  justify-content: center;
  opacity: 0.45;
  padding: 0.8vh 1.2vh;
}

/* Die gerade laufende Funktion wird hervorgehoben - das ist die einzige
   Rueckmeldung, die der Gast auf einen Druck der Wiedergabetaste bekommt. */
.vod-controls__button--active {
  background-color: rgba(241, 241, 241, 0.2);
  opacity: 1;
}

.vod-controls__speed {
  font-size: 2.2vh;
  margin-left: 0.6vh;
}

.vod-controls__hint {
  font-size: 2.2vh;
  margin-top: 2vh;
  opacity: 0.7;
  text-align: center;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/VideoPlayer/VodControls.vue